import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../../components/ui/sidebar';
import { RotateCcw, Settings } from 'lucide-react';
import { useSettingsStore } from '../../stores/settingsStore';
import { supabase } from '../../lib/supabase';
import LoadingSpinner from '../../components/ui/loading-spinner';
import { useAuthStore } from '../../stores/authStore';
import { WorkspaceSettings } from '../../components/ui/workspace-settings/workspace-settings';
import { PsTokensSection } from '../../components/ui/ps-tokens/ps-tokens-section';
import { PageHeader } from '../../components/ui/page-header/page-header';
import { useTutorialStore } from '../../stores/tutorialStore';
import { Button } from '../../components/ui/button/button';
import { Card, CardContent } from '../../components/ui/card';

export const ApiSettings = (): JSX.Element => {
  const navigate = useNavigate();
  const { user: authUser } = useAuthStore();
  const { loadSettings } = useSettingsStore();
  const { restartOnboarding } = useTutorialStore();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<
    Array<{ id: string; email: string; role: 'viewer' | 'admin' }>
  >([]);

  useEffect(() => {
    loadSettings(authUser?.currentWorkspace?.workspace_id);
    checkAdminStatus();
  }, [loadSettings, authUser]);

  const checkAdminStatus = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setIsAdmin(false);
        return;
      }

      // Get the current user's workspace role
      const isAdmin = authUser?.currentWorkspace?.role?.name === 'admin';
      setIsAdmin(isAdmin);

      // Fetch all workspace users (both admins and viewers can see team members)
      const { data: members, error: usersError } = await supabase
        .from('workspace_profiles')
        .select('profile_id, email, role:workspace_roles(name)')
        .eq('workspace_id', authUser?.currentWorkspace?.workspace_id)
        .order('created_at', { ascending: true });

      if (usersError) throw usersError;
      setUsers(
        members.map((member) => {
          const role = Array.isArray(member.role)
            ? member.role[0]
            : member.role;
          return {
            id: member.profile_id,
            email: member.email,
            role: role?.name || 'viewer',
          };
        }),
      );
    } catch (err) {
      console.error('Error checking admin status:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to check admin status',
      );
      setIsAdmin(false);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleUserAdmin = async (userId: string, makeAdmin: boolean) => {
    try {
      if (!isAdmin)
        throw new Error('Unauthorized: Only admins can change roles');

      const { data: roles, error: roleError } = await supabase
        .from('workspace_roles')
        .select('id, name');

      if (roleError) throw roleError;

      const targetRole = roles?.find((role) =>
        makeAdmin ? role.name === 'admin' : role.name === 'viewer',
      );

      if (!targetRole) throw new Error('Role not found');

      const { error: updateError } = await supabase
        .from('workspace_profiles')
        .update({ role_id: targetRole.id })
        .eq('profile_id', userId)
        .eq('workspace_id', authUser?.currentWorkspace?.workspace_id);

      if (updateError) throw updateError;

      setUsers(
        users.map((user) =>
          user.id === userId ? { ...user, role: targetRole.name } : user,
        ),
      );
    } catch (err) {
      console.error('Error updating admin status:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to update admin status',
      );
    }
  };

  const handleRestartOnboarding = () => {
    restartOnboarding();
    navigate('/agent-builder');
  };

  return (
    <div className="flex h-screen bg-prompt-safecomwhite">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="flex h-full w-full flex-col">
          <PageHeader
            title="Settings"
            icon={
              <Settings className="h-6 w-6 text-prompt-safecomoutrageous-orange" />
            }
          />
          {isLoading ? (
            <div className="flex justify-center pt-10">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="flex flex-col gap-8 p-6">
              {error && (
                <div className="rounded-lg border border-red-400 bg-red-50 px-4 py-3 text-sm text-red-700 shadow-sm">
                  <strong className="font-medium">Error:</strong> {error}
                </div>
              )}
              <div className="flex flex-col gap-8">
                <div className="space-y-4">
                  <div>
                    <h2 className="font-prompt-safe-com-semantic-heading-3 text-prompt-safecomvulcan">
                      Onboarding
                    </h2>
                    <p className="mt-1 text-sm text-prompt-safecomabbey">
                      Restart the guided setup from Agent Builder. Existing
                      workspace content stays unchanged.
                    </p>
                  </div>
                  <Card className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]">
                    <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-prompt-safecomvulcan">
                          Guided setup
                        </h3>
                        <p className="mt-1 text-sm text-prompt-safecomabbey">
                          Run the full Agent, Persona, Evaluator, Simulation,
                          Evaluation flow again.
                        </p>
                      </div>
                      <Button
                        type="button"
                        className="hover:bg-prompt-safecomoutrageous-orange/90 bg-prompt-safecomoutrageous-orange text-white"
                        onClick={handleRestartOnboarding}
                      >
                        <RotateCcw className="h-4 w-4" />
                        Redo onboarding
                      </Button>
                    </CardContent>
                  </Card>
                </div>

                {/* Workspace Settings Section */}
                <div className="space-y-4">
                  <div>
                    <h2 className="font-prompt-safe-com-semantic-heading-3 text-prompt-safecomvulcan">
                      Workspace settings
                    </h2>
                    <p className="mt-1 text-sm text-prompt-safecomabbey">
                      Manage your workspace, team members, and invitations.
                    </p>
                  </div>
                  <WorkspaceSettings
                    authUser={authUser}
                    users={users}
                    setUsers={setUsers}
                    isAdmin={isAdmin}
                    toggleUserAdmin={toggleUserAdmin}
                  />
                </div>

                <PsTokensSection authUser={authUser} />

                {/* API Key Management Section */}
                <div className="space-y-4">
                  <div className="opacity-60">
                    <h2 className="font-prompt-safe-com-semantic-heading-3 text-prompt-safecomvulcan">
                      API key management
                    </h2>
                    <p className="mt-1 text-sm text-prompt-safecomabbey">
                      Manage API keys for AI providers. These keys are stored
                      securely and used for your workspace.
                    </p>
                  </div>
                  <div className="border-prompt-safecomabbey/40 bg-prompt-safecomwhite-smoke rounded-lg border border-dashed p-6 text-center">
                    <p className="text-sm text-prompt-safecomabbey">
                      Bringing your own API key is available on request.{' '}
                      <a
                        href="mailto:info@sacher.ai"
                        className="hover:text-prompt-safecomoutrageous-orange/80 font-medium text-prompt-safecomoutrageous-orange underline"
                      >
                        Contact us
                      </a>{' '}
                      to enable this for your workspace.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
